<template>
  <div ref="pdfContent" class="pdf-content">
    <CCard class="border-0">
      <CCardHeader class="no-print bg-transparent">
        <div class="d-flex justify-content-end">
          <CButton size="sm" color="success" @click="exportPDF" :disabled="isGeneratingPDF">
            <CIcon name="cil-cloud-download" class="mr-1" />
            {{ isGeneratingPDF ? 'Generating...' : 'Download PDF' }}
          </CButton>
        </div>
      </CCardHeader>
      
      <CCardBody class="p-4">
        <!-- Header Section -->
        <div class="document-header">
          <CRow class="align-items-center mb-4">
            <CCol xs="12" sm="6" class="mb-3 mb-sm-0">
              <img
                v-if="getImageUrl()"
                :src="getImageUrl()"
                alt="Company Logo"
                class="company-logo"
              />
            </CCol>
            <CCol xs="12" sm="6" class="text-sm-right">
              <h1 class="document-title mb-2">RECEIPT</h1>
              <h5 class="company-name mb-1">{{ receipt.business?.name }}</h5>
              <p class="company-reg mb-1">{{ receipt.business?.regNo ? `(${receipt.business.regNo})` : '' }}</p>
              <div class="company-address mb-2" v-if="receipt.business?.address" v-html="formatAddress(receipt.business.address)"></div>
              <p class="company-phone" v-if="receipt.business?.phone">Tel: {{ receipt.business.phone }}</p>
            </CCol>
          </CRow>
          
          <hr class="section-divider" />
        </div>

        <!-- Customer and Document Details -->
        <div class="document-details mb-4">
          <CRow>
            <CCol xs="12" md="6" class="mb-3 mb-md-0">
              <div class="customer-section">
                <h6 class="section-title">Received From:</h6>
                <p class="customer-name mb-1">
                  <strong>{{ getCustomerName() }}</strong>
                </p>
                <div 
                  class="customer-address mb-2" 
                  v-if="getCustomerAddress()" 
                  v-html="formatAddress(getCustomerAddress())"
                ></div>
                <p class="customer-contact" v-if="getCustomerContactPersonName()">
                  Attn: <strong>{{ getCustomerContactPersonName() }}</strong>
                </p>
              </div>
            </CCol>
            
            <CCol xs="12" md="6">
              <div class="document-info">
                <table class="info-table">
                  <tbody>
                    <tr>
                      <td class="info-label">Receipt No:</td>
                      <td class="info-value">{{ receipt.incomeReceiptNumber }}</td>
                    </tr>
                    <tr>
                      <td class="info-label">Date:</td>
                      <td class="info-value">{{ getReceiptIssuedDate() }}</td>
                    </tr>
                    <tr v-if="receipt.reference">
                      <td class="info-label">Reference:</td>
                      <td class="info-value">{{ receipt.reference }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CCol>
          </CRow>
          
          <CRow v-if="receipt.title" class="mt-3">
            <CCol>
              <div class="receipt-title">
                <p><strong>Title: </strong>{{ receipt.title }}</p>
              </div>
            </CCol>
          </CRow>
        </div>

        <!-- Items Table -->
        <div class="items-section mb-4">
          <!-- Main Table (Desktop + Print/PDF) -->
          <div class="desktop-table">
            <table class="items-table table table-bordered">
              <thead>
                <tr>
                  <th class="text-center" style="width: 50px">#</th>
                  <th>Item & Description</th>
                  <th class="text-center" style="width: 100px">Quantity</th>
                  <th class="text-right" style="width: 120px">
                    Price (RM)
                  </th>
                  <th class="text-right" style="width: 120px">
                    Total (RM)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in computedReceiptItems" :key="item.id || item.position">
                  <td class="text-center">{{ item.position }}</td>
                  <td>
                    <div class="item-name mb-1">
                      <strong>{{ item.product?.name }}</strong>
                    </div>
                    <div 
                      class="item-description" 
                      v-if="item.description"
                      v-html="formatDescription(item.description)"
                    ></div>
                  </td>
                  <td class="text-center">{{ item.quantity }}</td>
                  <td class="text-right">{{ formatCurrency(item.price) }}</td>
                  <td class="text-right">{{ formatCurrency(item.totalAmountPerItem) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-grand-total">
                  <td colspan="4" class="text-right"><strong>Total Amount</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(grandTotal) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Mobile Cards (Only for small screens on web) -->
          <div class="mobile-cards d-md-none">
            <div 
              v-for="item in computedReceiptItems" 
              :key="item.id || item.position"
              class="item-card mb-3 p-3 border rounded"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="item-header">
                  <span class="item-number">#{{ item.position }}</span>
                  <h6 class="item-name mb-1">{{ item.product?.name }}</h6>
                </div>
                <div class="item-total text-right">
                  <small class="text-muted">Total</small>
                  <div class="font-weight-bold">{{ formatCurrency(item.totalAmountPerItem) }}</div>
                </div>
              </div>
              
              <div class="item-description mb-2" v-if="item.description" v-html="formatDescription(item.description)"></div>
              
              <CRow class="item-details">
                <CCol xs="6">
                  <small class="text-muted">Quantity</small>
                  <div>{{ item.quantity }}</div>
                </CCol>
                <CCol xs="6">
                  <small class="text-muted">Price</small>
                  <div>{{ formatCurrency(item.price) }}</div>
                </CCol>
              </CRow>
            </div>

            <!-- Mobile Summary -->
            <div class="mobile-summary p-3 bg-light rounded">
              <div class="d-flex justify-content-between">
                <span class="font-weight-bold">Total Amount:</span>
                <span class="font-weight-bold h5">{{ formatCurrency(grandTotal) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Information -->
        <div class="payment-section mb-4">
          <CRow>
            <CCol xs="12" md="6">
              <div class="payment-info">
                <h6 class="section-title mb-2">Payment Information</h6>
                <div class="payment-details p-3 bg-light rounded">
                  <div class="d-flex justify-content-between mb-2">
                    <span>Amount Received:</span>
                    <span class="font-weight-bold text-success">{{ formatCurrency(grandTotal) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2">
                    <span>Payment Method:</span>
                    <span>{{ receipt.paymentMethod || 'Cash' }}</span>
                  </div>
                  <div class="d-flex justify-content-between">
                    <span>Status:</span>
                    <span class="badge badge-success">Paid</span>
                  </div>
                </div>
              </div>
            </CCol>
            <CCol xs="12" md="6">
              <div class="receipt-summary">
                <h6 class="section-title mb-2">Receipt Summary</h6>
                <div class="summary-box p-3 border rounded">
                  <div class="summary-item d-flex justify-content-between mb-2">
                    <span>Items Count:</span>
                    <span class="font-weight-bold">{{ computedReceiptItems.length }}</span>
                  </div>
                  <div class="summary-item d-flex justify-content-between mb-2">
                    <span>Total Quantity:</span>
                    <span class="font-weight-bold">{{ getTotalQuantity() }}</span>
                  </div>
                  <hr class="my-2">
                  <div class="summary-item d-flex justify-content-between">
                    <span class="font-weight-bold">Total Amount:</span>
                    <span class="font-weight-bold h6 text-primary">{{ formatCurrency(grandTotal) }}</span>
                  </div>
                </div>
              </div>
            </CCol>
          </CRow>
        </div>

        <!-- Terms and Conditions -->
        <div v-if="receipt.note" class="terms-section mb-4">
          <h6 class="section-title mb-2">Terms and Conditions</h6>
          <div class="terms-content" v-html="formatNote(receipt.note)"></div>
        </div>

        <!-- Thank You Message -->
        <div class="thank-you-section text-center mb-4">
          <div class="thank-you-box p-4 bg-success text-white rounded">
            <h5 class="mb-2">Thank You!</h5>
            <p class="mb-0">We appreciate your business and look forward to serving you again.</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="document-footer text-center">
          <hr class="footer-divider">
          <div class="footer-content">
            <p class="company-info mb-1">
              {{ receipt.business?.name }} | {{ receipt.business?.website }} | {{ receipt.business?.phone }}
            </p>
            <p class="powered-by mb-0">
              Powered By 
              <img src="/logo.png" alt="Niaga-ku Logo" class="footer-logo" />
              Niaga-Ku.com
            </p>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";
import html2pdf from "html2pdf.js";

export default {
  name: "WidgetsReportIncomeReceipt",
  props: {
    receipt: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isGeneratingPDF: false,
    };
  },
  computed: {
    computedReceiptItems() {
      return (this.receipt.items || []).map((item) => {
        return {
          ...item,
          productName: item.product?.name || "",
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },
    grandTotal() {
      return this.computedReceiptItems.reduce((acc, item) => {
        return acc + (item.totalAmountPerItem || 0);
      }, 0);
    },
  },
  mounted() {
    this.forceA4Size();
  },
  methods: {
    // Amount without currency; the column headers say RM
    formatCurrency(amount) {
      return formatMyr(amount);
    },

    getTotalQuantity() {
      return this.computedReceiptItems.reduce((total, item) => {
        return total + (parseFloat(item.quantity) || 0);
      }, 0);
    },

    formatNote(note) {
      return note ? note.replace(/\n/g, "<br />") : "";
    },

    formatDescription(description) {
      if (!description) return "";
      return description
        .split("\n")
        .map((line) => {
          const indentLevel = (line.match(/^(\s+)/) || [""])[0].length / 4;
          return `<p style="padding-left: ${
            indentLevel * 20
          }px; margin-bottom: 0.5rem;">${line.trim()}</p>`;
        })
        .join("");
    },

    getTotalItemPrice(item) {
      try {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        return quantity * price;
      } catch (error) {
        return 0;
      }
    },

    getReceiptIssuedDate() {
      return this.receipt.issuedDate 
        ? formatDate(this.receipt.issuedDate)
        : "";
    },

    getCustomerAddress() {
      try {
        return this.receipt.customer?.address || "";
      } catch (error) {
        return "";
      }
    },

    getCustomerContactPersonName() {
      try {
        return this.receipt.contactName || this.receipt.customer?.contactName || "";
      } catch (error) {
        return "";
      }
    },

    getCustomerName() {
      try {
        return this.receipt.customer?.name || "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    removeTrailingSlash(str) {
      return str && str.endsWith("/") ? str.slice(0, -1) : str;
    },

    getImageUrl() {
      try {
        if (this.receipt.business?.logoUrl) {
          return this.removeTrailingSlash(apiUrl) + this.receipt.business.logoUrl;
        }
        return "";
      } catch (error) {
        return "";
      }
    },

    formatAddress(address) {
      return address ? address.replace(/\n/g, "<br />") : "";
    },

    forceA4Size() {
      const pdfContent = this.$refs.pdfContent;
      if (pdfContent) {
        pdfContent.style.width = "210mm";
        pdfContent.style.minHeight = "297mm";
      }
    },

    printContent() {
      this.forceA4Size();
      setTimeout(() => {
        const originalTitle = document.title;
        document.title = `${this.receipt.business?.shortName || 'Receipt'}_${this.receipt.incomeReceiptNumber}`;

        const printContents = this.$refs.pdfContent.innerHTML;
        const originalContents = document.body.innerHTML;

        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        document.title = originalTitle;

        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }, 100);
    },

    async exportPDF() {
      this.isGeneratingPDF = true;

      try {
        // Clone the content and remove no-print elements
        const element = this.$refs.pdfContent.cloneNode(true);
        const noPrintElements = element.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());

        // Apply PDF-specific styling
        element.style.width = '794px'; // A4 width in pixels
        element.style.minHeight = 'auto';
        element.style.padding = '20px';
        element.style.fontSize = '14px';
        element.style.lineHeight = '1.4';
        element.style.fontFamily = 'Arial, sans-serif';
        element.style.backgroundColor = '#ffffff';

        // Override mobile styles for consistent PDF output
        const logo = element.querySelector('.company-logo');
        if (logo) {
          logo.style.maxWidth = '180px';
          logo.style.height = 'auto';
          logo.style.display = 'block';
        }

        // Fix typography
        const h1 = element.querySelector('.document-title');
        if (h1) {
          h1.style.fontSize = '28px';
          h1.style.margin = '0 0 10px 0';
        }

        const h5 = element.querySelector('.company-name');
        if (h5) {
          h5.style.fontSize = '16px';
          h5.style.margin = '0 0 5px 0';
        }

        // Fix row and column layouts
        const rows = element.querySelectorAll('.row');
        rows.forEach(row => {
          row.style.display = 'flex';
          row.style.flexWrap = 'wrap';
          row.style.margin = '0 -7.5px';
        });

        const cols = element.querySelectorAll('.col');
        cols.forEach(col => {
          col.style.flex = '1';
          col.style.padding = '0 7.5px';
        });

        // Fix text alignment
        const textRight = element.querySelectorAll('.text-right, .text-sm-right');
        textRight.forEach(el => {
          el.style.textAlign = 'right';
        });

        // Hide mobile cards in PDF
        const mobileCards = element.querySelectorAll('.mobile-cards');
        mobileCards.forEach(el => {
          el.style.display = 'none';
        });

        // Configure html2pdf options
        const opt = {
          margin: [10, 0, 10, 0],
          filename: `${this.receipt.business?.shortName || 'Receipt'}_${this.receipt.incomeReceiptNumber}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'portrait'
          },
          pagebreak: { mode: ['css', 'legacy'] }
        };

        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error('PDF generation failed:', error);
        this.$toast.error("Couldn't create the PDF. Try again in a moment.");
      } finally {
        this.isGeneratingPDF = false;
      }
    },
  },
};
</script>

<style scoped>
.pdf-content {
  width: 210mm;
  min-height: 297mm;
  max-width: 100%;
  margin: 0 auto;
  padding: 20mm;
  box-sizing: border-box;
  background-color: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.4;
  color: #333;
}

.document-header {
  margin-bottom: 2rem;
}

.company-logo {
  max-width: 200px;
  max-height: 80px;
  width: auto;
  height: auto;
}

.document-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #27ae60;
  margin-bottom: 0.5rem;
}

.company-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #34495e;
}

.company-reg, .company-address, .company-phone {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin-bottom: 0.25rem;
}

.section-divider {
  border: 2px solid #27ae60;
  margin: 1.5rem 0;
}

.document-details {
  margin-bottom: 2rem;
}

.section-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.customer-name {
  font-size: 1.1rem;
  color: #2c3e50;
}

.customer-address, .customer-contact {
  font-size: 0.9rem;
  color: #5a6c7d;
}

.info-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 0.25rem;
}

.info-label {
  font-weight: 600;
  color: #2c3e50;
  padding-right: 1rem;
  white-space: nowrap;
  vertical-align: top;
}

.info-value {
  color: #5a6c7d;
  vertical-align: top;
}

.receipt-title {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.25rem;
  border-left: 4px solid #27ae60;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.items-table th {
  background-color: #27ae60;
  color: white;
  font-weight: 600;
  padding: 0.75rem 0.5rem;
  border: 1px solid #229954;
}

.items-table td {
  padding: 0.75rem 0.5rem;
  border: 1px solid #bdc3c7;
  vertical-align: top;
}

.items-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.item-name {
  font-weight: 600;
  color: #2c3e50;
}

.item-description {
  color: #5a6c7d;
  font-size: 0.9rem;
}

.table-grand-total {
  background-color: #d5f4e6 !important;
  font-size: 1.1rem;
}

/* Desktop table - always visible except on small mobile screens */
.desktop-table {
  display: block;
}

/* Mobile cards - only show on small screens */
.mobile-cards {
  display: none;
}

/* Mobile Styles */
.item-card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef !important;
}

.item-number {
  background-color: #27ae60;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.item-name {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.item-total {
  text-align: right;
}

.item-details {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dee2e6;
}

.item-details small {
  font-weight: 600;
  color: #6c757d;
}

.mobile-summary {
  background-color: #e8f5e8 !important;
  border: 1px solid #27ae60;
}

/* Payment Section */
.payment-section {
  margin-bottom: 2rem;
}

.payment-details {
  background-color: #e8f5e8 !important;
  border: 1px solid #27ae60;
}

.summary-box {
  border: 1px solid #dee2e6 !important;
  background-color: #f8f9fa;
}

.summary-item {
  font-size: 0.9rem;
}

/* Thank You Section */
.thank-you-section {
  margin: 2rem 0;
}

.thank-you-box {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%) !important;
  border: none;
  box-shadow: 0 4px 8px rgba(39, 174, 96, 0.2);
}

.terms-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
}

.terms-content {
  color: #5a6c7d;
  font-size: 0.9rem;
}

.document-footer {
  margin-top: 3rem;
  padding-top: 1.5rem;
}

.footer-divider {
  border-top: 1px solid #bdc3c7;
  margin-bottom: 1rem;
}

.company-info {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.powered-by {
  color: #7f8c8d;
  font-size: 0.8rem;
}

.footer-logo {
  max-width: 20px;
  height: auto;
  filter: grayscale(100%);
  margin: 0 0.25rem;
}

/* Print Styles */
@media print {
  .no-print {
    display: none !important;
  }

  .pdf-content {
    width: 100%;
    min-height: auto;
    margin: 0;
    padding: 15mm;
    box-shadow: none;
  }

  .document-title {
    font-size: 2rem;
  }

  /* Force desktop table to show in print */
  .desktop-table {
    display: block !important;
  }

  /* Hide mobile cards in print */
  .mobile-cards {
    display: none !important;
  }

  .items-table {
    page-break-inside: auto;
    width: 100%;
    table-layout: fixed;
  }

  .items-table tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  .items-table thead {
    display: table-header-group;
  }

  .items-table tfoot {
    display: table-footer-group;
  }

  .items-table th,
  .items-table td {
    font-size: 10px;
    padding: 6px 4px;
  }

  .items-table th:nth-child(1),
  .items-table td:nth-child(1) {
    width: 8%;
  }

  .items-table th:nth-child(2),
  .items-table td:nth-child(2) {
    width: 50%;
  }

  .items-table th:nth-child(3),
  .items-table td:nth-child(3) {
    width: 12%;
  }

  .items-table th:nth-child(4),
  .items-table td:nth-child(4) {
    width: 15%;
  }

  .items-table th:nth-child(5),
  .items-table td:nth-child(5) {
    width: 15%;
  }

  .payment-section,
  .thank-you-section,
  .terms-section {
    page-break-inside: avoid;
  }

  .document-footer {
    page-break-inside: avoid;
  }
}

/* Responsive Design - Only affect web view */
@media screen and (max-width: 768px) {
  .pdf-content {
    width: 100%;
    padding: 15px;
    min-height: auto;
  }

  .document-title {
    font-size: 1.8rem;
  }

  .company-logo {
    max-width: 150px;
    max-height: 60px;
  }

  .section-title {
    font-size: 0.95rem;
  }

  .info-table {
    font-size: 0.9rem;
  }

  /* Show mobile cards on small screens (web only) */
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }
}

@media screen and (max-width: 576px) {
  .pdf-content {
    padding: 10px;
  }

  .document-title {
    font-size: 1.5rem;
  }

  .company-logo {
    max-width: 120px;
    max-height: 50px;
  }

  .receipt-title {
    padding: 0.75rem;
  }

  .payment-section {
    margin-bottom: 1.5rem;
  }

  .thank-you-section {
    margin: 1.5rem 0;
  }

  .terms-section {
    padding: 1rem;
  }

  /* Ensure mobile cards are shown on very small screens (web only) */
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }
}
</style>